import { subscriptionsApi } from '../../api/client';
import { trackPixelEvent } from '../../components/FacebookPixelTracker';
import { ONBOARDING_PATH, isLikelyApiPlanId } from '../../utils/onboarding';
import type { Plan } from './types';
import { DEFAULT_PLANS, derivePlanFromSubscription, isFreePlan } from './types';
import type { PricingState } from './usePricingState';
import {
  comparePlanChange,
  buildUpgradeSuccessMessage,
  buildDowngradeSuccessMessage,
  buildNeutralPlanChangeMessage,
  getFreeTierFromPlans,
} from './planChange';
import { needsDowngradeResourceWizard, targetPlanLimits } from './downgradeCompliance';
import { loadSiteContentSnapshots } from './loadDowngradeSnapshots';

interface HandlerDependencies {
  state: PricingState;
  navigate: (path: string) => void;
  isAuthenticated: boolean;
  /** True on `/onboarding` — payment method required before dashboard/building. */
  isOnboardingFlow: boolean;
}

export const createPricingHandlers = ({
  state,
  navigate,
  isAuthenticated,
  isOnboardingFlow,
}: HandlerDependencies) => {
  const getCurrentPlanForCompare = (): Plan | null => {
    const p = state.currentSubscription?.plan;
    if (p) return p;
    return getFreeTierFromPlans(state.plans).plan;
  };

  const needsPaymentOnboarding = (): boolean => {
    const status = state.onboardingStatus;
    if (!status) {
      return isOnboardingFlow;
    }
    return status.requiresPaymentSetup || !status.completed;
  };

  const showCheckoutError = (message: string, durationMs = 5000) => {
    state.setCheckoutError(message);
    state.setShowErrorToast(true);
    setTimeout(() => state.setShowErrorToast(false), durationMs);
  };

  const ensurePlansReadyForCheckout = (): boolean => {
    if (state.plansLoadedFromApi) {
      return true;
    }
    showCheckoutError(
      'Unable to load billing plans from the server. Please refresh the page and try again.'
    );
    return false;
  };

  const resolveFreePlanId = (): string | null => {
    const { plan } = getFreeTierFromPlans(state.plans);
    return isLikelyApiPlanId(plan.id) ? plan.id : null;
  };

  const redirectToSetupCheckout = async (planId: string, options?: { onboarding?: boolean }) => {
    const onboarding = options?.onboarding ?? isOnboardingFlow;
    const successUrl = onboarding
      ? `${window.location.origin}/dashboard?setup=complete`
      : `${window.location.origin}/pricing?setup=complete`;
    const cancelUrl = onboarding
      ? `${window.location.origin}${ONBOARDING_PATH}`
      : `${window.location.origin}/pricing`;

    const { checkoutUrl } = await subscriptionsApi.createSetupCheckout({
      planId,
      successUrl,
      cancelUrl,
    });
    window.location.href = checkoutUrl;
  };

  const startPaymentOnboarding = async () => {
    if (!ensurePlansReadyForCheckout()) {
      return;
    }
    const freePlanId = resolveFreePlanId();
    if (!freePlanId) {
      showCheckoutError(
        'The Free plan is not configured in billing. Please contact support to finish setup.'
      );
      return;
    }
    await redirectToSetupCheckout(freePlanId, { onboarding: isOnboardingFlow });
  };

  const needsPaymentMethodFirst = (): boolean => {
    const status = state.onboardingStatus;
    return Boolean(status?.completed && !status.hasPaymentMethod);
  };

  const loadPricingData = async () => {
    try {
      state.setLoading(true);

      let loadedPlans: Plan[] = [];
      try {
        const plansData = await subscriptionsApi.getPlans();
        loadedPlans = plansData;
        state.setPlans(plansData);
        state.setPlansLoadedFromApi(true);
      } catch {
        state.setPlans(DEFAULT_PLANS);
        loadedPlans = DEFAULT_PLANS;
        state.setPlansLoadedFromApi(false);
      }

      if (isAuthenticated) {
        try {
          const onboarding = await subscriptionsApi.getOnboardingStatus();
          state.setOnboardingStatus(onboarding);
        } catch {
          state.setOnboardingStatus(null);
        }

        try {
          const subscription = await subscriptionsApi.getCurrentSubscription();

          if (subscription) {
            if (subscription.usage?.plan?.name && subscription.usage.plan.name !== null) {
              state.setCurrentSubscription({
                ...subscription,
                plan: subscription.usage.plan,
              });
            } else if (subscription.plan?.name) {
              state.setCurrentSubscription(subscription);
            } else if (!subscription.plan && subscription.subscription) {
              const planId = subscription.subscription.planId || subscription.subscription.planName;
              const planName = subscription.subscription.planName;
              const derivedPlan = derivePlanFromSubscription(planId, planName, loadedPlans);

              state.setCurrentSubscription({
                ...subscription,
                plan: derivedPlan,
              });
            } else if (!subscription.subscription && subscription.usage?.plan) {
              state.setCurrentSubscription({
                ...subscription,
                plan: subscription.usage.plan,
              });
            } else {
              state.setCurrentSubscription(subscription);
            }
          }
        } catch (error: any) {
          console.error('Failed to load current subscription:', error);
          state.setCurrentSubscription({
            subscription: null,
            plan: DEFAULT_PLANS[0],
            usage: { sites: 0, pages: 0 },
            limits: {
              maxSites: 1,
              maxPagesPerSite: 5,
              canUseDomain: false,
              canUseContactForms: false,
              canUseAdvancedComponents: false,
              canUseCompeteStudio: false,
            },
          });
        }
      }
    } catch (error) {
      console.error('Failed to load pricing data:', error);
    } finally {
      state.setLoading(false);
    }
  };

  const executePlanChange = async (
    planId: string,
    options?: { intent?: 'upgrade' | 'downgrade' | 'same' }
  ) => {
    const token = localStorage.getItem('authToken');

    if (!isAuthenticated && !isOnboardingFlow && !token) {
      navigate('/login');
      return;
    }

    const selectedPlan = state.plans.find(p => p.id === planId);
    if (!selectedPlan) {
      return;
    }

    if (needsPaymentOnboarding()) {
      try {
        state.setUpgrading(planId);
        await startPaymentOnboarding();
      } catch (error: any) {
        console.error('Failed to start payment onboarding:', error);
        showCheckoutError(error.message || 'Failed to start payment setup');
      } finally {
        state.setUpgrading(null);
      }
      return;
    }

    if (!ensurePlansReadyForCheckout()) {
      return;
    }

    if (!isLikelyApiPlanId(planId)) {
      showCheckoutError('Invalid plan configuration. Please refresh the page and try again.');
      return;
    }

    const current = getCurrentPlanForCompare();
    const resolvedIntent = options?.intent ?? comparePlanChange(current, selectedPlan, state.plans);

    const successCopy = (() => {
      if (resolvedIntent === 'downgrade') return buildDowngradeSuccessMessage(selectedPlan);
      if (resolvedIntent === 'upgrade') return buildUpgradeSuccessMessage(selectedPlan);
      return buildNeutralPlanChangeMessage(selectedPlan);
    })();

    try {
      state.setUpgrading(planId);

      const selectedPlanForPixel = selectedPlan;
      if (selectedPlanForPixel) {
        const price =
          state.billingPeriod === 'yearly'
            ? (selectedPlanForPixel.priceYearly ?? selectedPlanForPixel.priceMonthly * 12)
            : selectedPlanForPixel.priceMonthly;
        trackPixelEvent('InitiateCheckout', {
          content_name: selectedPlanForPixel.displayName || selectedPlanForPixel.name,
          value: price,
          currency: 'USD',
        });
      }

      const successQuery = new URLSearchParams({
        success: 'true',
        planDisplayName: selectedPlan.displayName,
        message: successCopy,
      });
      const successUrl = `${window.location.origin}/dashboard?${successQuery.toString()}`;

      if (needsPaymentMethodFirst() && !isFreePlan(selectedPlan)) {
        const freePlanId = resolveFreePlanId();
        if (!freePlanId) {
          showCheckoutError('Add a payment method before upgrading. Please contact support.');
          return;
        }
        await redirectToSetupCheckout(freePlanId);
        return;
      }

      const hasPaymentMethod =
        state.currentSubscription?.subscription?.stripeCustomerId &&
        (state.currentSubscription?.subscription?.status === 'active' ||
          state.currentSubscription?.subscription?.canceledAt);

      if (hasPaymentMethod) {
        try {
          await subscriptionsApi.updateSubscription({
            planId,
            billingPeriod: state.billingPeriod,
          });

          if (selectedPlanForPixel) {
            const purchasePrice =
              state.billingPeriod === 'yearly'
                ? (selectedPlanForPixel.priceYearly ?? selectedPlanForPixel.priceMonthly * 12)
                : selectedPlanForPixel.priceMonthly;
            trackPixelEvent('Purchase', {
              content_name: selectedPlanForPixel.displayName || selectedPlanForPixel.name,
              value: purchasePrice,
              currency: 'USD',
            });
          }

          navigate('/dashboard?' + successQuery.toString());
          return;
        } catch {
          // Fall back to checkout
        }
      }

      const { checkoutUrl } = await subscriptionsApi.createCheckoutSession({
        planId,
        billingPeriod: state.billingPeriod,
        successUrl,
        cancelUrl: `${window.location.origin}/pricing`,
      });
      window.location.href = checkoutUrl;
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);

      if (error.status === 402 && error.data?.error === 'PAYMENT_METHOD_REQUIRED') {
        try {
          await startPaymentOnboarding();
          return;
        } catch (setupError) {
          console.error('Failed to start setup checkout:', setupError);
        }
      }

      const tokenAtError = localStorage.getItem('authToken');

      if (error.status === 401) {
        if (!tokenAtError && !isOnboardingFlow) {
          showCheckoutError('Please log in to select a plan.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          showCheckoutError('Authentication error. Please try logging in again.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setTimeout(() => navigate('/login'), 2000);
        }
      } else if (error.status === 404) {
        if (isOnboardingFlow) {
          showCheckoutError(
            'Billing is unavailable right now. Please try again in a moment or contact support.',
            7000
          );
        } else {
          showCheckoutError(
            `The subscription service is not available. Please try again later or contact support.`,
            7000
          );
        }
      } else if (error.status === 500) {
        showCheckoutError('Server error occurred. Please try again later.');
      } else {
        showCheckoutError(error.message || 'Failed to create checkout session');
      }
    } finally {
      state.setUpgrading(null);
    }
  };

  const handleUpgrade = (planId: string) => {
    const token = localStorage.getItem('authToken');

    if (!isAuthenticated && !isOnboardingFlow && !token) {
      navigate('/login');
      return;
    }

    const selectedPlan = state.plans.find(p => p.id === planId);
    if (!selectedPlan) {
      return;
    }

    if (selectedPlan && isFreePlan(selectedPlan)) {
      const hasActiveSubscription =
        state.currentSubscription?.subscription?.status === 'active' ||
        state.currentSubscription?.subscription?.status === 'trialing';
      const isCancelled = state.currentSubscription?.subscription?.canceledAt != null;

      if (hasActiveSubscription && !isCancelled) {
        state.setShowDowngradeModal(true);
        return;
      } else if (isCancelled) {
        navigate(
          '/dashboard?message=' +
            encodeURIComponent(
              'Your subscription is already set to cancel at the end of your billing period.'
            )
        );
        return;
      }
    }

    if (needsPaymentOnboarding()) {
      void (async () => {
        state.setUpgrading(planId);
        try {
          await startPaymentOnboarding();
        } catch (error: any) {
          console.error('Failed to start payment onboarding:', error);
          showCheckoutError(error.message || 'Failed to start payment setup');
        } finally {
          state.setUpgrading(null);
        }
      })();
      return;
    }

    if (selectedPlan && isFreePlan(selectedPlan)) {
      void (async () => {
        state.setUpgrading(planId);
        try {
          const freePlanId = resolveFreePlanId();
          if (!freePlanId) {
            showCheckoutError('The Free plan is not configured. Please contact support.');
            return;
          }
          await redirectToSetupCheckout(freePlanId);
        } catch (error: any) {
          console.error('Failed to start setup checkout:', error);
          showCheckoutError(error.message || 'Failed to start payment setup');
        } finally {
          state.setUpgrading(null);
        }
      })();
      return;
    }

    const current = getCurrentPlanForCompare();
    const change = comparePlanChange(current, selectedPlan, state.plans);

    if (change === 'upgrade') {
      state.setPlanChangeModal({ kind: 'upgrade', planId });
      return;
    }
    if (change === 'downgrade') {
      state.setPlanChangeModal({ kind: 'paid-downgrade', planId });
      return;
    }

    void executePlanChange(planId, { intent: 'same' });
  };

  const closePlanChangeModal = () => {
    state.setPlanChangeModal(null);
  };

  const performCancelToFree = async () => {
    const result = await subscriptionsApi.cancelSubscription();
    state.setShowDowngradeModal(false);
    const cancelDate = result.cancelAt
      ? new Date(result.cancelAt).toLocaleDateString()
      : 'the end of your billing period';
    navigate('/dashboard?downgraded=true&cancelDate=' + encodeURIComponent(cancelDate));
  };

  const completeDowngradeResourceWizard = () => {
    const w = state.downgradeResourceWizard;
    if (!w) return;
    state.setDowngradeResourceWizard(null);
    if (w.mode === 'free') {
      void (async () => {
        state.setDowngrading(true);
        state.setDowngradeError(null);
        try {
          await performCancelToFree();
        } catch (apiError: any) {
          if (apiError.status === 400) {
            const msg =
              apiError?.data?.message ||
              apiError?.message ||
              'You are still over the limit for the Free plan. Remove more content and try again.';
            state.setDowngradeError(msg);
            state.setShowDowngradeModal(true);
          } else if (apiError.status === 404) {
            state.setDowngradeError(
              "You're already on the Free plan. There's no active subscription to cancel."
            );
            state.setShowDowngradeModal(true);
          } else {
            state.setDowngradeError(
              apiError?.message ||
                'Unable to cancel subscription. Please try again or contact support.'
            );
            state.setShowDowngradeModal(true);
          }
        } finally {
          state.setDowngrading(false);
        }
      })();
    } else if (w.planId) {
      void executePlanChange(w.planId, { intent: 'downgrade' });
    }
  };

  const handleConfirmPlanChange = async () => {
    const pending = state.planChangeModal;
    if (!pending) return;
    if (pending.kind === 'upgrade') {
      const id = pending.planId;
      state.setPlanChangeModal(null);
      void executePlanChange(id, { intent: 'upgrade' });
      return;
    }
    if (pending.kind === 'paid-downgrade') {
      const id = pending.planId;
      const selectedPlan = state.plans.find(p => p.id === id);
      if (!selectedPlan) return;
      state.setDowngradePreviewLoading(true);
      try {
        const snapshots = await loadSiteContentSnapshots();
        const { maxSites, maxPagesPerSite } = targetPlanLimits(selectedPlan);
        if (needsDowngradeResourceWizard(snapshots, maxSites, maxPagesPerSite)) {
          state.setPlanChangeModal(null);
          state.setDowngradeResourceWizard({
            mode: 'paid',
            planId: id,
            planDisplayName: selectedPlan.displayName,
            maxSites,
            maxPagesPerSite,
            snapshots,
          });
          return;
        }
        state.setPlanChangeModal(null);
        void executePlanChange(id, { intent: 'downgrade' });
      } catch (e) {
        console.error('Downgrade preview failed:', e);
      } finally {
        state.setDowngradePreviewLoading(false);
      }
    }
  };

  const handleConfirmDowngrade = async () => {
    try {
      state.setDowngradeError(null);
      state.setDowngradePreviewLoading(true);
      const { maxSites, maxPagesPerSite, displayName } = getFreeTierFromPlans(state.plans);
      const snapshots = await loadSiteContentSnapshots();
      if (needsDowngradeResourceWizard(snapshots, maxSites, maxPagesPerSite)) {
        state.setShowDowngradeModal(false);
        state.setDowngradeResourceWizard({
          mode: 'free',
          planDisplayName: displayName,
          maxSites,
          maxPagesPerSite,
          snapshots,
        });
        return;
      }
      state.setDowngrading(true);
      try {
        await performCancelToFree();
        state.setDowngrading(false);
      } catch (apiError: any) {
        if (apiError.status === 404) {
          state.setDowngradeError(
            "You're already on the Free plan. There's no active subscription to cancel."
          );
        } else if (apiError.status === 400) {
          const serverMsg = apiError?.data?.message;
          state.setDowngradeError(
            typeof serverMsg === 'string' && serverMsg.length > 0
              ? serverMsg
              : 'Your subscription data appears to be invalid. Please contact support for assistance.'
          );
        } else if (apiError.status === 401) {
          state.setDowngradeError(
            'Your session has expired. Please refresh the page and try again.'
          );
        } else if (apiError.status === 500) {
          try {
            const { portalUrl } = await subscriptionsApi.createPortalSession({
              returnUrl: window.location.origin + '/dashboard?downgraded=true',
            });
            window.location.href = portalUrl;
          } catch (portalError: any) {
            if (portalError.status === 404) {
              state.setDowngradeError(
                "You don't have a Stripe subscription to manage. If you believe this is an error, please contact support."
              );
            } else {
              state.setDowngradeError(
                'Unable to access the billing portal. Please try again later or contact support.'
              );
            }
          }
        } else {
          state.setDowngradeError(
            apiError.message ||
              'Unable to cancel subscription. Please try again or contact support.'
          );
        }
        state.setDowngrading(false);
      }
    } catch (error) {
      console.error('Unexpected error during downgrade:', error);
      state.setDowngradeError('An unexpected error occurred. Please try again later.');
      state.setDowngrading(false);
    } finally {
      state.setDowngradePreviewLoading(false);
    }
  };

  const closeDowngradeModal = () => {
    state.setShowDowngradeModal(false);
    state.setDowngradeError(null);
    state.setDowngrading(false);
  };

  const isCurrentPlan = (plan: Plan): boolean => {
    if (!state.currentSubscription) return false;

    if (state.currentSubscription.plan?.name) {
      const currentPlan = state.currentSubscription.plan;

      if (currentPlan.id && plan.id && currentPlan.id === plan.id) return true;

      if (
        currentPlan.name &&
        plan.name &&
        currentPlan.name.toUpperCase() === plan.name.toUpperCase()
      ) {
        return true;
      }

      return false;
    }

    if (state.currentSubscription.subscription) {
      const sub = state.currentSubscription.subscription;
      const planIdOrName = sub.planId || sub.planName;

      if (planIdOrName) {
        if (plan.id === planIdOrName) return true;
        if (plan.name && planIdOrName.toUpperCase() === plan.name.toUpperCase()) return true;

        const planIdUpper = planIdOrName.toUpperCase();
        const planNameUpper = plan.name?.toUpperCase();

        if (
          (planIdUpper.includes('STARTER') || planIdUpper === 'STARTER') &&
          (planNameUpper === 'STARTER' || plan.id === 'starter-plan')
        ) {
          return true;
        }

        if (
          (planIdUpper.includes('PROFESSIONAL') || planIdUpper === 'PROFESSIONAL') &&
          (planNameUpper === 'PROFESSIONAL' || plan.id === 'professional-plan')
        ) {
          return true;
        }

        if (
          (planIdUpper.includes('FREE') || planIdUpper === 'FREE') &&
          (planNameUpper === 'FREE' || plan.id === 'free-plan')
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const getButtonText = (plan: Plan): string => {
    if (!isAuthenticated) return 'Sign Up';
    if (isOnboardingFlow && needsPaymentOnboarding()) {
      return isFreePlan(plan) ? 'Add Card & Start Free' : 'Add Card to Continue';
    }
    if (isOnboardingFlow && isFreePlan(plan)) return 'Start with Free';

    if (isCurrentPlan(plan)) {
      const isCancelled = state.currentSubscription?.subscription?.canceledAt != null;
      if (isCancelled && state.currentSubscription?.subscription) {
        return (
          'Cancelled (Ends ' +
          new Date(state.currentSubscription.subscription.currentPeriodEnd).toLocaleDateString() +
          ')'
        );
      }
      return 'Your Current Plan';
    }

    let currentPlanPrice = 0;
    if (state.currentSubscription?.plan) {
      if (state.currentSubscription.plan.priceMonthly !== undefined) {
        currentPlanPrice = state.currentSubscription.plan.priceMonthly;
      } else if (state.currentSubscription.plan.name) {
        const currentPlanName = state.currentSubscription.plan.name.toUpperCase();
        const matchingPlan = state.plans.find(p => p.name.toUpperCase() === currentPlanName);
        if (matchingPlan) {
          currentPlanPrice = matchingPlan.priceMonthly || 0;
        } else {
          if (currentPlanName === 'PROFESSIONAL') currentPlanPrice = 99;
          else if (currentPlanName === 'STARTER') currentPlanPrice = 29;
          else currentPlanPrice = 0;
        }
      }
    }

    const targetPlanPrice = plan.priceMonthly || 0;

    if (targetPlanPrice < currentPlanPrice) return 'Downgrade';
    else if (targetPlanPrice > currentPlanPrice) return 'Upgrade';
    else return 'Switch Plan';
  };

  const closeDowngradeResourceWizard = () => {
    state.setDowngradeResourceWizard(null);
  };

  return {
    loadPricingData,
    handleUpgrade,
    handleConfirmDowngrade,
    closeDowngradeModal,
    handleConfirmPlanChange,
    closePlanChangeModal,
    completeDowngradeResourceWizard,
    closeDowngradeResourceWizard,
    isCurrentPlan,
    getButtonText,
  };
};
